import { PublicClientApplication } from "@azure/msal-browser";
import * as Sentry from "@sentry/react";
import axios from "axios";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";

import { supportedLocales } from "../config/locales.js";
import { createLocalAxios } from "../lib/api.js";
import apiMessages from "../lib/apiMessages.js";
import {
  AUTHENTICATION_MISSING_TOKEN,
  AUTHENTICATION_NOTOK,
  AUTHENTICATION_TOO_MANY_ATTEMPTS,
  AuthenticationError,
  CurrentUserError,
} from "../utils/sessionErrors.js";

import { selectUserId } from "./selectors.js";
import {
  sessionDestroy,
  setLocale,
  setAccessToken,
  setPermissions,
  setCurrentUser,
  setPermissionsFetched,
} from "./sessionSlice.js";

const { VITE_DRIVE_API_HOST } = import.meta.env;
const BASE_URL = VITE_DRIVE_API_HOST;

/**
 * Sets the access token.
 */
export const updateAccessToken = (token) => {
  return async (dispatch) => {
    if (!token) return;

    try {
      const exp = jwtDecode(token).exp;
      const cur = Date.now() / 1000;

      if (exp && exp < cur) {
        console.error("[OI] updateAccessToken token expired");
        return;
      }
    } catch (error) {
      console.error(`[OI] updateAccessToken decode error=${error}`);
      return;
    }

    localStorage.accessToken = token;

    return dispatch(setAccessToken(token));
  };
};

/**
 * Authenticates against the API.
 */
export const authenticate = (username, password) => {
  return async (dispatch) => {
    const endpoint = `${BASE_URL}auth/token`;
    let response;
    try {
      response = await axios.post(endpoint, { email: username, password });
    } catch (error) {
      console.error("Error during authentication:", error);

      const errorCode = error.response?.data?.error;

      switch (errorCode) {
        case apiMessages.ERROR_REACHED_FAILED_LOGIN_ATTEMPTS:
          throw new AuthenticationError(
            "Too many login attempts.",
            AUTHENTICATION_TOO_MANY_ATTEMPTS,
          );
        case apiMessages.ERROR_USER_DOES_NOT_EXIST:
        case apiMessages.ERROR_INVALID_CREDENTIALS:
        case apiMessages.ERROR_BAD_REQUEST:
          throw new AuthenticationError(
            "Invalid credentials.",
            AUTHENTICATION_NOTOK,
          );
        default:
          Sentry.withScope((scope) => {
            scope.setExtras({ username });
            Sentry.captureException(error);
          });
          throw error;
      }
    }

    const result = response.data;

    if (!result.token) {
      console.error("Missing token:", result, response);
      throw new AuthenticationError(
        "Missing token.",
        AUTHENTICATION_MISSING_TOKEN,
        [],
      );
    }

    await dispatch(updateAccessToken(result.token));
    await dispatch(fetchCurrentUser());

    return result.token;
  };
};

export const fetchCurrentUser = () => {
  return async (dispatch, getState) => {
    const state = getState();
    const userId = selectUserId(state);

    if (!userId) return;

    const localAxios = createLocalAxios(getState);

    let response = {};
    try {
      response = await localAxios.get("account/user");
    } catch (error) {
      console.error("Error fetching the current user:", error);
      throw new CurrentUserError({});
    }

    if (!response.data.userData || !response.data.permissions) {
      throw new CurrentUserError({});
    }

    const user = response.data.userData;

    if ("permissions" in response.data) {
      await dispatch(setPermissions(response.data.permissions));
    }
    await dispatch(setPermissionsFetched());

    dispatch(setCurrentUser(user));
    return user;
  };
};

export const authenticateSSO = (idToken, msal) => {
  return async (dispatch, getState) => {
    const localAxios = createLocalAxios(getState);

    let response = {};
    try {
      response = await localAxios.post(
        "auth/token-by-oidc",
        {},
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            Accept: "application/json",
          },
        },
      );
    } catch (error) {
      throw new AuthenticationError(
        "Authentication failed.",
        AUTHENTICATION_NOTOK,
        error.response?.data?.error || error.message,
      );
    }

    const { token } = response.data || {};
    if (!token) {
      throw new AuthenticationError(
        "Missing token.",
        AUTHENTICATION_MISSING_TOKEN,
        [],
      );
    }

    if (msal?.config) {
      localStorage.msalInstance = JSON.stringify({
        auth: msal.config.auth,
        cache: msal.config.cache,
      });
    }

    localStorage.accessToken = token;

    await dispatch(setAccessToken(token));
    await dispatch(fetchCurrentUser());

    return Promise.resolve();
  };
};

const clearMsalStorage = () => {
  delete localStorage.msalInstance;
  Object.keys(localStorage)
    .filter((k) => k.startsWith("msal."))
    .forEach((k) => localStorage.removeItem(k));
};

export const destroy = () => async (dispatch) => {
  const localMsalInstance = localStorage.msalInstance; // capture first

  try {
    delete localStorage.accessToken;
    clearMsalStorage();
  } catch (e) {
    console.error("[OI] destroy ERROR: could not delete from localStorage", e);
  }

  try {
    if (localMsalInstance) {
      const msalConfiguration = JSON.parse(localMsalInstance);
      const msal = new PublicClientApplication(msalConfiguration);
      await msal.logout();
    }
  } catch (e) {
    console.error("[OI] destroy MSAL failed", e);
  }

  await dispatch(sessionDestroy());
};

export const resume = () => {
  return async (dispatch) => {
    const accessToken = localStorage.accessToken;

    if (!accessToken) {
      console.log("[OI] resume no accessToken...");
      await dispatch(destroy());
      return;
    }

    const isExpired = (() => {
      try {
        const { exp } = jwtDecode(accessToken);
        return exp && exp < Date.now() / 1000;
      } catch {
        return true;
      }
    })();

    if (isExpired) {
      // SSO user
      if (localStorage.msalInstance) {
        // @TODO: add logic for SSO token background refresh
      }

      // user logged in with credentials
      // @TODO: add refresh token logic here as well for normal sign-in
      await dispatch(destroy());
      return;
    }

    await dispatch(setAccessToken(accessToken));
    await dispatch(fetchCurrentUser());
  };
};

export const hasPermissions =
  (resourceType, resourceKey) => (dispatch, getState) => {
    const permissions = getState().session?.permissions || [];
    return permissions.some(
      (permission) =>
        permission.resourceType === resourceType &&
        permission.resourceKey === resourceKey,
    );
  };

export const updateLocale = (locale) => async (dispatch) => {
  if (!supportedLocales.includes(locale)) return;

  /**
   * Protect against accessing `document` outside browser environments.
   */
  try {
    if (document) {
      Cookies.set("locale_cookie", locale, { expires: 365 });
    }
  } catch (error) {
    console.error(
      `[OI] setLocale('${locale}'): failed to set cookie: ${error}`,
    );
  }

  return dispatch(setLocale(locale));
};
